import React from "react";
import { useNavigate } from "react-router-dom";
import { ButtonShadcn } from "@/components/ui/Button";
import { useAuth } from "@/hooks/useAuth";
import { resolveLandingPath } from "@/routes/landing";

/**
 * Unauthorized
 *
 * The 403 screen: shown by PermissionGuard when a route needs a permission or
 * a module the account does not hold, and mounted directly at /unauthorized.
 *
 * The action is what makes this an answer rather than a dead end — it is the
 * one screen a user reaches with no menu context and nothing else to click.
 * Where it goes comes from `resolveLandingPath`, so it is a screen this
 * particular account can open. Hard-coding a dashboard here is what put a
 * user without `dashboard.view` in a loop between two 403s.
 *
 * Props:
 * - redirectTo: string — override the resolved destination. Rarely needed;
 *   both live callers let it resolve itself, which is why it is not passed in.
 */
const Unauthorized = ({
    redirectTo
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const destination = typeof redirectTo === "string" && redirectTo.startsWith("/") ? redirectTo : resolveLandingPath(user);

    return (
        <div className="relative w-full flex-1">
            <div className="absolute inset-0 bg-background-primary/60 backdrop-blur-sm z-10 flex flex-col items-center justify-center">
                <div className="text-center max-w-lg px-4">
                    <div className="text-text-primary text-6xl font-bold opacity-90">403</div>
                    <h2 className="mt-4 text-lg font-semibold text-text-primary">Unauthorized</h2>
                    <p className="mt-2 text-sm text-text-primary/70">
                        You don't have permission to access this page. Ask your administrator if you need it.
                    </p>

                    {/* The default variant pairs bg-button-primary with
                        --primary-foreground, which has no dark-mode override —
                        white on white. text-invert is the token that actually
                        flips with the theme, so this reads in both. */}
                    <div className="mt-6 flex justify-center">
                        <ButtonShadcn
                            size="lg"
                            className="bg-button-primary text-text-invert hover:bg-button-primary/85"
                            onClick={() => navigate(destination, { replace: true })}
                        >
                            Take me back
                        </ButtonShadcn>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Unauthorized;
